import {
  createMinus,
  createPlay,
  createPlus,
  createRefreshCcw,
  createRingPause,
  createRingPlay,
  createTrash,
} from '@shared/assets/icons';
import {
  Button,
  InteractableComponent,
  ItemsCount,
  Modal,
  Option,
  Select,
  Text,
  TextArea,
  TextChip,
} from '@shared/components';
import {
  AWKWARD_MOMENT_TYPE_OPTIONS,
  AwkwardMomentType,
  getAwkwardMomentTypeLabel,
  isAwkwardMomentType,
} from 'clerk_common/types/awkwardMoment';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions';
import ZoomPlugin from 'wavesurfer.js/dist/plugins/zoom';

export type AwkwardMomentLabelRange = {
  id: string;
  startSeconds: number;
  endSeconds: number;
  awkwardMomentType: AwkwardMomentType;
  note?: string;
};

export type AwkwardMomentLabelValue = {
  ranges: AwkwardMomentLabelRange[];
};

export type AwkwardMomentLabelSaveResult = {
  failedRangeIds?: string[];
};

type AwkwardMomentSelection = Omit<
  AwkwardMomentLabelRange,
  'awkwardMomentType'
> & {
  id: string;
  awkwardMomentType?: AwkwardMomentType;
};

type AwkwardMomentLabelModalProps = {
  isOpen: boolean;
  initialPlaybackSeconds: number;
  audioUrl?: string;
  onClose: () => void;
  onSave: (
    value: AwkwardMomentLabelValue
  ) => Promise<AwkwardMomentLabelSaveResult | void>;
};

const MIN_SELECTION_SECONDS = 0.25;
const ACTIVE_SELECTION_COLOR = 'rgba(37, 99, 235, 0.26)';
const INACTIVE_SELECTION_COLOR = 'rgba(100, 116, 139, 0.18)';
const WAVEFORM_HEIGHT = 56;
const DEFAULT_SELECTION_SECONDS = 5;
const ZOOM_MIN = 1;
const ZOOM_MAX = 500;
const ZOOM_STEP = 25;

export function AwkwardMomentLabelModal({
  isOpen,
  initialPlaybackSeconds,
  audioUrl,
  onClose,
  onSave,
}: AwkwardMomentLabelModalProps) {
  if (!isOpen) return null;

  return (
    <AwkwardMomentLabelModalContent
      initialPlaybackSeconds={initialPlaybackSeconds}
      audioUrl={audioUrl}
      onClose={onClose}
      onSave={onSave}
    />
  );
}

type AwkwardMomentLabelModalContentProps = Omit<
  AwkwardMomentLabelModalProps,
  'isOpen'
>;

function AwkwardMomentLabelModalContent({
  initialPlaybackSeconds,
  audioUrl,
  onClose,
  onSave,
}: AwkwardMomentLabelModalContentProps) {
  const [waveformContainer, setWaveformContainer] =
    useState<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const regionsPluginRef = useRef<RegionsPlugin | null>(null);
  const regionsByIdRef = useRef<Map<string, Region>>(new Map());
  const activeRegionIdRef = useRef<string | null>(null);
  const regionIdCounterRef = useRef(0);
  const [selections, setSelections] = useState<AwkwardMomentSelection[]>([]);
  const [activeRegionId, setActiveRegionId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isWaveformLoading, setIsWaveformLoading] = useState(Boolean(audioUrl));
  const [waveformError, setWaveformError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(initialPlaybackSeconds);
  const [zoom, setZoom] = useState(ZOOM_MIN);

  const activeSelection = selections.find(
    (selection) => selection.id === activeRegionId
  );
  const activeSelectionIndex = selections.findIndex(
    (selection) => selection.id === activeRegionId
  );
  const isActiveSelectionValid = activeSelection
    ? isValidRange(activeSelection.startSeconds, activeSelection.endSeconds)
    : false;
  const areSelectionsValid =
    selections.length > 0 &&
    selections.every((selection) =>
      isValidRange(selection.startSeconds, selection.endSeconds)
    );
  const areSelectionsTyped = selections.every((selection) =>
    Boolean(selection.awkwardMomentType)
  );
  const canSave = areSelectionsValid && areSelectionsTyped;
  const saveButtonLabel = getSaveButtonLabel(isSaving, selections.length);

  const makeRegionId = () => {
    regionIdCounterRef.current += 1;
    return `awkward-selection-${regionIdCounterRef.current}`;
  };

  const getRegionRange = useCallback((region: Region) => {
    const fallbackDuration = Math.max(
      region.end ?? region.start + MIN_SELECTION_SECONDS,
      region.start + MIN_SELECTION_SECONDS
    );
    return normalizeRange(
      region.start,
      region.end ?? region.start + MIN_SELECTION_SECONDS,
      wavesurferRef.current?.getDuration() ?? fallbackDuration
    );
  }, []);

  const upsertSelectionFromRegion = useCallback(
    (region: Region) => {
      const range = getRegionRange(region);
      setSelections((prev) => {
        const existingIndex = prev.findIndex(
          (selection) => selection.id === region.id
        );
        const next =
          existingIndex === -1
            ? [
                ...prev,
                {
                  id: region.id,
                  startSeconds: range.start,
                  endSeconds: range.end,
                },
              ]
            : prev.map((selection, index) =>
                index === existingIndex
                  ? {
                      ...selection,
                      startSeconds: range.start,
                      endSeconds: range.end,
                    }
                  : selection
              );
        return next.sort((a, b) => a.startSeconds - b.startSeconds);
      });
    },
    [getRegionRange]
  );

  const handleSave = async () => {
    if (!canSave) return;

    setIsSaving(true);
    try {
      const saveResult = await onSave({
        ranges: selections
          .map((selection) => ({
            id: selection.id,
            startSeconds: selection.startSeconds,
            endSeconds: selection.endSeconds,
            awkwardMomentType: selection.awkwardMomentType!,
            note: selection.note?.trim() || undefined,
          }))
          .sort((a, b) => a.startSeconds - b.startSeconds),
      });

      const failedRangeIds = new Set(saveResult?.failedRangeIds ?? []);
      if (failedRangeIds.size > 0) {
        const failedSelections = selections.filter((selection) =>
          failedRangeIds.has(selection.id)
        );

        regionsByIdRef.current.forEach((region, regionId) => {
          if (!failedRangeIds.has(regionId)) region.remove();
        });

        const nextActiveRegionId = failedSelections[0]?.id ?? null;
        setSelections(failedSelections);
        activeRegionIdRef.current = nextActiveRegionId;
        setActiveRegionId(nextActiveRegionId);
        return;
      }

      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const previewSelection = () => {
    if (!wavesurferRef.current || !activeSelection || !isActiveSelectionValid) {
      return;
    }
    void wavesurferRef.current.play(
      activeSelection.startSeconds,
      activeSelection.endSeconds
    );
  };

  const togglePlayPause = () => {
    void wavesurferRef.current?.playPause();
  };

  const applyZoom = (newZoom: number) => {
    const clampedZoom = clampZoom(newZoom);
    setZoom(clampedZoom);
    wavesurferRef.current?.zoom(clampedZoom);
  };

  const selectRegion = (region: Region) => {
    activeRegionIdRef.current = region.id;
    setActiveRegionId(region.id);
  };

  const selectSelection = (selection: AwkwardMomentSelection) => {
    activeRegionIdRef.current = selection.id;
    setActiveRegionId(selection.id);
    regionsByIdRef.current.get(selection.id)?.setOptions({
      color: ACTIVE_SELECTION_COLOR,
    });
  };

  const addSelection = () => {
    const regions = regionsPluginRef.current;
    const wavesurfer = wavesurferRef.current;
    if (!regions || !wavesurfer || duration <= 0) return;

    const selectionLength = Math.min(DEFAULT_SELECTION_SECONDS, duration);
    const start = Math.min(
      currentTime,
      Math.max(0, duration - selectionLength)
    );
    const range = normalizeRange(start, start + selectionLength, duration);
    const region = regions.addRegion({
      id: makeRegionId(),
      start: range.start,
      end: range.end,
      color: ACTIVE_SELECTION_COLOR,
      drag: true,
      resize: true,
      minLength: MIN_SELECTION_SECONDS,
    });
    selectRegion(region);
  };

  const removeSelection = (selectionId: string) => {
    const region = regionsByIdRef.current.get(selectionId);
    if (region) {
      region.remove();
      return;
    }

    setSelections((prev) =>
      prev.filter((selection) => selection.id !== selectionId)
    );
    if (selectionId === activeRegionId) {
      activeRegionIdRef.current = null;
      setActiveRegionId(null);
    }
  };

  const removeActiveSelection = () => {
    if (!activeRegionId) return;
    removeSelection(activeRegionId);
  };

  const resetSelection = () => {
    const regions = regionsPluginRef.current;
    if (!regions || duration <= 0) return;

    regions.clearRegions();
    const playbackSeconds = clampPlaybackSeconds(
      initialPlaybackSeconds,
      duration
    );
    activeRegionIdRef.current = null;
    setSelections([]);
    setActiveRegionId(null);
    setCurrentTime(playbackSeconds);
    wavesurferRef.current?.setTime(playbackSeconds);
  };

  const updateActiveSelectionNote = (selectionNote: string) => {
    if (!activeRegionId) return;
    setSelections((prev) =>
      prev.map((selection) =>
        selection.id === activeRegionId
          ? { ...selection, note: selectionNote }
          : selection
      )
    );
  };

  const updateActiveSelectionType = (value: AwkwardMomentType) => {
    if (!activeRegionId || !isAwkwardMomentType(value)) return;
    setSelections((prev) =>
      prev.map((selection) =>
        selection.id === activeRegionId
          ? { ...selection, awkwardMomentType: value }
          : selection
      )
    );
  };

  useEffect(() => {
    activeRegionIdRef.current = activeRegionId;
    regionsByIdRef.current.forEach((region, regionId) => {
      region.setOptions({
        color:
          regionId === activeRegionId
            ? ACTIVE_SELECTION_COLOR
            : INACTIVE_SELECTION_COLOR,
      });
    });
  }, [activeRegionId]);

  useEffect(() => {
    if (activeRegionId || selections.length === 0) return;
    const nextSelection = selections[0];
    activeRegionIdRef.current = nextSelection.id;
    setActiveRegionId(nextSelection.id);
  }, [activeRegionId, selections]);

  useEffect(() => {
    if (!audioUrl || !waveformContainer) return;

    let destroyed = false;
    let disableDragSelection: (() => void) | undefined;
    const regions = RegionsPlugin.create();
    const zoomPlugin = ZoomPlugin.create({
      maxZoom: ZOOM_MAX,
      deltaThreshold: 4,
    });
    regionsPluginRef.current = regions;
    const wavesurfer = WaveSurfer.create({
      container: waveformContainer,
      waveColor: '#94A3B8',
      progressColor: '#2563EB',
      cursorColor: '#111827',
      cursorWidth: 2,
      height: WAVEFORM_HEIGHT,
      normalize: false,
      backend: 'WebAudio',
      hideScrollbar: false,
      splitChannels: [
        {
          waveColor: '#CBD5E1',
          progressColor: '#3B82F6',
        },
        {
          waveColor: '#CBD5E1',
          progressColor: '#3B82F6',
        },
      ],
      plugins: [regions, zoomPlugin],
    });

    wavesurferRef.current = wavesurfer;
    setIsWaveformLoading(true);
    setWaveformError(null);
    setSelections([]);
    setActiveRegionId(null);

    const updateSelectedRegion = (region: Region) => {
      upsertSelectionFromRegion(region);
    };

    const unsubscribers = [
      wavesurfer.on('ready', (loadedDuration) => {
        if (destroyed) return;
        const playbackSeconds = clampPlaybackSeconds(
          initialPlaybackSeconds,
          loadedDuration
        );
        setDuration(loadedDuration);
        setCurrentTime(playbackSeconds);
        wavesurfer.setTime(playbackSeconds);

        disableDragSelection = regions.enableDragSelection(
          {
            color: ACTIVE_SELECTION_COLOR,
            drag: true,
            resize: true,
            minLength: MIN_SELECTION_SECONDS,
          },
          5
        );
        setIsWaveformLoading(false);
      }),
      wavesurfer.on('play', () => !destroyed && setIsPlaying(true)),
      wavesurfer.on('pause', () => !destroyed && setIsPlaying(false)),
      wavesurfer.on('finish', () => !destroyed && setIsPlaying(false)),
      wavesurfer.on('timeupdate', (time) => {
        if (!destroyed) setCurrentTime(time);
      }),
      wavesurfer.on('zoom', (minPxPerSec) => {
        if (!destroyed) setZoom(clampZoom(minPxPerSec));
      }),
      wavesurfer.on('error', (error) => {
        if (destroyed || error?.name === 'AbortError') return;
        setIsWaveformLoading(false);
        setWaveformError('Could not load recording waveform.');
        console.error('WaveSurfer error:', error);
      }),
      regions.on('region-created', (region) => {
        if (destroyed) return;
        regionsByIdRef.current.set(region.id, region);
        region.setOptions({
          color: ACTIVE_SELECTION_COLOR,
          drag: true,
          resize: true,
        });
        selectRegion(region);
        upsertSelectionFromRegion(region);
      }),
      regions.on('region-update', updateSelectedRegion),
      regions.on('region-updated', updateSelectedRegion),
      regions.on('region-removed', (region) => {
        if (destroyed) return;
        regionsByIdRef.current.delete(region.id);
        setSelections((prev) =>
          prev.filter((selection) => selection.id !== region.id)
        );
        if (region.id === activeRegionIdRef.current) {
          activeRegionIdRef.current = null;
        }
        setActiveRegionId((currentRegionId) =>
          currentRegionId === region.id ? null : currentRegionId
        );
      }),
      regions.on('region-clicked', (region, event) => {
        event.stopPropagation();
        selectRegion(region);
        region.play(true);
      }),
    ];

    const proxiedUrl = `/api/audio-proxy?url=${encodeURIComponent(audioUrl)}`;
    void wavesurfer.load(proxiedUrl).catch((error) => {
      if (destroyed || error?.name === 'AbortError') return;
      setIsWaveformLoading(false);
      setWaveformError('Could not load recording waveform.');
      console.error('WaveSurfer load failed:', error);
    });

    return () => {
      destroyed = true;
      disableDragSelection?.();
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      regionsPluginRef.current = null;
      regionsByIdRef.current.clear();
      wavesurferRef.current = null;
      wavesurfer.destroy();
    };
  }, [
    audioUrl,
    initialPlaybackSeconds,
    upsertSelectionFromRegion,
    waveformContainer,
  ]);

  return (
    <Modal.Root open>
      <Modal.Content
        size="large"
        className="flex max-h-[calc(100vh-2rem)] flex-col"
        onKeyDown={handleKeyDown}
      >
        <Modal.Header
          title="Label awkward moment"
          hasCloseButton
          onClose={onClose}
          className="shrink-0 px-6"
        />
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto overflow-x-hidden px-6 pb-6">
          {audioUrl ? (
            <div className="flex min-w-0 flex-col gap-3 rounded border border-gray-200 p-3">
              <div
                ref={setWaveformContainer}
                className="min-h-[7rem] min-w-0 overflow-hidden rounded border border-gray-200 bg-white"
              />
              {isWaveformLoading && (
                <Text type="body-xs" className="text-gray-600">
                  Loading waveform...
                </Text>
              )}
              {waveformError && (
                <Text type="body-xs" className="text-red-600">
                  {waveformError}
                </Text>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Text type="body-xs" className="text-gray-600">
                  Playback {formatTimestamp(currentTime)} /{' '}
                  {formatTimestamp(duration)}
                </Text>
                <div className="flex flex-wrap items-center gap-1">
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={addSelection}
                    isDisabled={isWaveformLoading || Boolean(waveformError)}
                    icon={createPlus}
                    aria-label="Add selection"
                    tooltip="Add selection"
                  />
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={togglePlayPause}
                    isDisabled={isWaveformLoading || Boolean(waveformError)}
                    icon={isPlaying ? createRingPause : createRingPlay}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    tooltip={isPlaying ? 'Pause' : 'Play'}
                  />
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={previewSelection}
                    isDisabled={
                      !isActiveSelectionValid ||
                      isWaveformLoading ||
                      Boolean(waveformError)
                    }
                    icon={createPlay}
                    aria-label="Play selection"
                    tooltip="Play selection"
                  />
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={removeActiveSelection}
                    isDisabled={
                      !activeRegionId ||
                      isWaveformLoading ||
                      Boolean(waveformError)
                    }
                    icon={createTrash}
                    aria-label="Delete selection"
                    tooltip="Delete selection"
                  />
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={resetSelection}
                    isDisabled={isWaveformLoading || Boolean(waveformError)}
                    icon={createRefreshCcw}
                    aria-label="Reset to clicked time"
                    tooltip="Reset to clicked time"
                  />
                </div>
              </div>
              {duration > 0 && (
                <div className="flex flex-col gap-2">
                  <ZoomControl
                    value={zoom}
                    onChange={(event) => applyZoom(Number(event.target.value))}
                    onZoomOut={() => applyZoom(zoom - ZOOM_STEP)}
                    onZoomIn={() => applyZoom(zoom + ZOOM_STEP)}
                    onReset={() => applyZoom(ZOOM_MIN)}
                  />
                </div>
              )}
              {selections.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Text type="body-xs" className="text-gray-600">
                    Selections
                  </Text>
                  {selections.map((selection, index) => (
                    <div
                      key={selection.id}
                      className={clsx(
                        'flex items-center gap-3 rounded border px-3 py-2',
                        selection.id === activeRegionId
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white'
                      )}
                    >
                      <InteractableComponent
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                        onPress={() => selectSelection(selection)}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <ItemsCount
                            count={index + 1}
                            className="h-6 min-w-6 shrink-0 bg-gray-100"
                            textClassName="text-gray-700"
                          />
                          <Text
                            as="span"
                            type="body-sm"
                            className="min-w-0 text-gray-800"
                          >
                            {formatTimestamp(selection.startSeconds)} -{' '}
                            {formatTimestamp(selection.endSeconds)}
                          </Text>
                          {selection.awkwardMomentType && (
                            <TextChip
                              className="shrink-0"
                              text={getAwkwardMomentTypeLabel(
                                selection.awkwardMomentType
                              )}
                              shouldCapitalize={false}
                            />
                          )}
                        </div>
                      </InteractableComponent>
                      <Button
                        variant="secondary"
                        size="small"
                        onPress={() => removeSelection(selection.id)}
                        icon={createTrash}
                        aria-label={`Delete selection ${index + 1}`}
                        tooltip="Delete selection"
                      />
                    </div>
                  ))}
                </div>
              )}
              {activeSelection && (
                <div className="flex flex-col gap-3">
                  <Select<AwkwardMomentType>
                    label={
                      activeSelectionIndex === -1
                        ? 'Awkward moment type'
                        : `Selection ${
                            activeSelectionIndex + 1
                          } awkward moment type`
                    }
                    placeholder="Select type"
                    size="small"
                    value={activeSelection.awkwardMomentType}
                    onChange={updateActiveSelectionType}
                  >
                    {AWKWARD_MOMENT_TYPE_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                  <TextArea
                    label={
                      activeSelectionIndex === -1
                        ? 'Selection notes'
                        : `Selection ${activeSelectionIndex + 1} notes`
                    }
                    rows={2}
                    value={activeSelection.note ?? ''}
                    onChange={updateActiveSelectionNote}
                    autoresizes
                  />
                </div>
              )}
            </div>
          ) : (
            <Text type="body-xs" className="text-red-600">
              Recording is not available for range selection.
            </Text>
          )}
          {!canSave && (
            <Text type="body-xs" className="text-red-600">
              {getValidationMessage(selections, areSelectionsTyped)}
            </Text>
          )}
        </div>
        <Modal.Footer className="shrink-0">
          <Modal.FooterButton
            variant="secondary"
            onPress={onClose}
            isDisabled={isSaving}
          >
            Cancel
          </Modal.FooterButton>
          <Modal.FooterButton
            isPrimary
            onPress={handleSave}
            isDisabled={isSaving || !canSave}
          >
            {saveButtonLabel}
          </Modal.FooterButton>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}

function normalizeRange(start: number, end: number, duration: number) {
  const safeDuration =
    Number.isFinite(duration) && duration > MIN_SELECTION_SECONDS
      ? duration
      : Math.max(end, start + MIN_SELECTION_SECONDS);
  const maxStart = Math.max(0, safeDuration - MIN_SELECTION_SECONDS);
  const normalizedStart = Math.min(Math.max(0, start), maxStart);
  const normalizedEnd = Math.min(
    Math.max(normalizedStart + MIN_SELECTION_SECONDS, end),
    safeDuration
  );

  return {
    start: roundToTenth(normalizedStart),
    end: roundToTenth(normalizedEnd),
  };
}

function roundToTenth(value: number) {
  return Math.round(value * 10) / 10;
}

function isValidRange(startSeconds: number, endSeconds: number) {
  return (
    Number.isFinite(startSeconds) &&
    Number.isFinite(endSeconds) &&
    startSeconds >= 0 &&
    endSeconds > startSeconds
  );
}

function getValidationMessage(
  selections: AwkwardMomentSelection[],
  areSelectionsTyped: boolean
) {
  if (selections.length === 0) return 'Add at least one selection.';
  if (!areSelectionsTyped) return 'Choose a type for each selection.';
  return 'Each selection must end after it starts.';
}

function getSaveButtonLabel(isSaving: boolean, selectionCount: number): string {
  if (isSaving) return 'Saving...';
  if (selectionCount === 1) return 'Save label';
  return `Save ${selectionCount} labels`;
}

function clampZoom(value: number) {
  return Math.min(Math.max(value, ZOOM_MIN), ZOOM_MAX);
}

function clampPlaybackSeconds(value: number, duration: number) {
  if (!Number.isFinite(value)) return 0;
  if (!Number.isFinite(duration) || duration <= 0) return Math.max(0, value);
  return Math.min(Math.max(0, value), duration);
}

function ZoomControl({
  value,
  onChange,
  onZoomOut,
  onZoomIn,
  onReset,
}: {
  value: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onZoomOut: () => void;
  onZoomIn: () => void;
  onReset: () => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <Text type="body-xs" className="mr-1 text-gray-600">
        Zoom
      </Text>
      <Button
        variant="secondary"
        size="small"
        onPress={onZoomOut}
        isDisabled={value <= ZOOM_MIN}
        icon={createMinus}
        aria-label="Zoom out"
        tooltip="Zoom out"
      />
      <input
        aria-label="Zoom waveform"
        type="range"
        min={ZOOM_MIN}
        max={ZOOM_MAX}
        step={ZOOM_STEP}
        value={value}
        onChange={onChange}
        className="accent-brand-500 h-6 w-24"
      />
      <Text type="body-xs" className="w-10 text-right font-mono text-gray-600">
        {Math.round(value)}x
      </Text>
      <Button
        variant="secondary"
        size="small"
        onPress={onZoomIn}
        isDisabled={value >= ZOOM_MAX}
        icon={createPlus}
        aria-label="Zoom in"
        tooltip="Zoom in"
      />
      <Button
        variant="secondary"
        size="small"
        onPress={onReset}
        icon={createRefreshCcw}
        aria-label="Fit waveform"
        tooltip="Fit waveform"
      />
    </div>
  );
}

function formatTimestamp(seconds: number) {
  if (!Number.isFinite(seconds)) return '0:00';
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(wholeSeconds / 60);
  const secs = wholeSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
